'use client';

import { clientOnlyDynamic } from '@/lib/client-only-dynamic';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import DeferredChrome from '@/components/DeferredChrome';
import GoogleOfferwall from '@/components/GoogleOfferwall';
import { MarketRatesProvider } from '@/components/market/MarketRatesProvider';
import NavigationSkeletonGate from '@/components/skeletons/NavigationSkeletonGate';
import type { SiteNavigation } from '@/lib/getCategories';
import type { FooterTag } from '@/lib/get-footer-trending-tags';
import { SITE_SHELL_TOP, SITE_SHELL_TOP_STRIP } from '@/lib/site-layout';

function PageSlot({ children, blocking }: { children: React.ReactNode; blocking?: boolean }) {
  // Bot render'ı: Suspense yok → SSR shell erken flush edilmez, notFound()
  // gerçek 404 status dönebilir. DOM yapısı gate ile birebir aynı tutulur.
  if (blocking) {
    return (
      <div className="relative min-w-0">
        <div>{children}</div>
      </div>
    );
  }
  // Suspense YOK: içerik ana HTML akışıyla tek seferde gelir. Boundary burada
  // olsaydı sayfa ayrı stream parçası olur, hard reload'da önce boş kabuk
  // sonra içerik boyanırdı (zıplama). useSearchParams gereksinimi gate içinde
  // yalnızca null render eden controller'ın Suspense'iyle karşılanır.
  return <NavigationSkeletonGate>{children}</NavigationSkeletonGate>;
}

const WebPushListener = clientOnlyDynamic(() => import('@/components/WebPushListener'));
const WebPushPrompt = clientOnlyDynamic(() => import('@/components/WebPushPrompt'));
const GoogleAdManager = clientOnlyDynamic(() => import('@/components/GoogleAdManager'));

interface ConditionalLayoutProps {
  children: React.ReactNode;
  /** Bot UA — bloklayan SSR (doğru HTTP status için) */
  crawlerMode?: boolean;
  siteNavigation: SiteNavigation;
  footerTrendingTags?: FooterTag[];
  footerWeekTags?: FooterTag[];
  footerTodayTags?: FooterTag[];
  showShortsNav?: boolean;
  showUserSubmissions?: boolean;
}

function SiteMain({
  children,
  hasStrip,
}: {
  children: React.ReactNode;
  hasStrip: boolean;
}) {
  return (
    <div
      id="main-content"
      tabIndex={-1}
      className={cn(
        'site-shell-main w-full min-w-0 max-w-full pb-[max(4rem,calc(env(safe-area-inset-bottom,0px)+3.25rem))] sm:pb-[max(5rem,calc(env(safe-area-inset-bottom,0px)+3.5rem))]',
        hasStrip ? cn('site-shell-with-strip', SITE_SHELL_TOP_STRIP) : SITE_SHELL_TOP,
      )}
    >
      {children}
    </div>
  );
}

export default function ConditionalLayout({
  children,
  crawlerMode = false,
  siteNavigation,
  footerTrendingTags = [],
  footerWeekTags = [],
  footerTodayTags = [],
  showShortsNav = true,
  showUserSubmissions = true,
}: ConditionalLayoutProps) {
  const pathname = usePathname();
  const { headerCategories, stripItems, menuCategories } = siteNavigation;
  // HeaderBottomStrip her zaman "Son Dakika" ekler — padding strip ile hizalı kalsın
  const hasStrip = true;

  const isAdminPage = pathname?.startsWith('/admin');
  const isEditorXPage = pathname === '/editor-x';
  const isShortsPage = pathname?.startsWith('/shorts');

  // Login / kayıt / şifre sayfalarında site header + footer gösterilir.
  if (isAdminPage || isEditorXPage || isShortsPage) {
    return (
      <>
        <GoogleOfferwall />
        <PageSlot blocking={crawlerMode}>{children}</PageSlot>
      </>
    );
  }

  return (
    <MarketRatesProvider>
      <GoogleOfferwall />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[200] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-gray-900 focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#D92E2E] dark:focus:bg-slate-900 dark:focus:text-white"
      >
        İçeriğe atla
      </a>
      <Header
        headerCategories={headerCategories}
        stripItems={stripItems}
        menuCategories={menuCategories}
        showShortsNav={showShortsNav}
      />
      <SiteMain hasStrip={hasStrip}>
        <PageSlot blocking={crawlerMode}>{children}</PageSlot>
      </SiteMain>
      <DeferredChrome fallbackMs={4_000}>
        <GoogleAdManager />
      </DeferredChrome>
      <Footer
        categories={menuCategories}
        trendingTags={footerTrendingTags}
        weekTags={footerWeekTags}
        todayTags={footerTodayTags}
        showUserSubmissions={showUserSubmissions}
      />
      <DeferredChrome fallbackMs={5_500}>
        <WebPushListener />
        <WebPushPrompt />
      </DeferredChrome>
    </MarketRatesProvider>
  );
}
